import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface ExecutionRow {
  readonly testExecutionId: string;
  readonly executionCode: string;
  readonly testCaseId: string;
  readonly environment: string;
  readonly result: string;
  readonly status: string;
}

const RESULTS = ['', 'PASS', 'FAIL', 'BLOCKED', 'SKIPPED', 'NOT_RUN'];
const ENVIRONMENTS = ['', 'DEV', 'SIT', 'UAT', 'PREPROD', 'PROD'];
const CREATE_ENVIRONMENTS = ['DEV', 'SIT', 'UAT', 'PREPROD', 'PROD'];
const RECORD_RESULTS = ['PASS', 'FAIL', 'BLOCKED', 'SKIPPED'];

/**
 * M34 — Execution Console (§31 / §11). Register of test-execution records (T451)
 * with the recorded result, environment and the not-started→in-progress→completed
 * lifecycle. Supports scheduling an execution and driving it — record a result,
 * approve and cancel — each wired to the guarded /v1/qms/test-executions write
 * API. Filterable by result and environment.
 */
export function ExecutionConsole(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<ExecutionRow[]>([]);
  const [result, setResult] = useState('');
  const [environment, setEnvironment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newCaseId, setNewCaseId] = useState('');
  const [newEnv, setNewEnv] = useState('SIT');
  const [results, setResults] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (result) params.set('result', result);
    if (environment) params.set('environment', environment);
    const qs = params.toString();
    apiRequest<{ data: ExecutionRow[] }>(`/v1/qms/test-executions${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, result, environment, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m34.common.notAuthorised', { perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newCaseId.trim()) { setActionError(t('m34.exec.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/qms/test-executions`, {
        method: 'POST',
        body: { testCaseId: newCaseId.trim(), environment: newEnv },
        token,
      });
      setNotice(t('m34.exec.scheduled', { id: newCaseId.trim() }));
      setNewCaseId('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_EXECUTION_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: ExecutionRow, path: string, label: string, perm: string, body?: Record<string, unknown>): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/test-executions/${r.testExecutionId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m34.exec.notice', { code: r.executionCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  return (
    <main aria-label="execution-console">
      <h1>{t('m34.exec.title')}</h1>

      <section aria-label="create-execution">
        <h2>{t('m34.exec.scheduleHeading')}</h2>
        <form aria-label="execution-form" onSubmit={(e) => void create(e)}>
          <label>{t('m34.exec.testCase')}<input aria-label="field-execution-case" value={newCaseId} onChange={(e) => setNewCaseId(e.target.value)} /></label>
          <label>{t('m34.exec.environment')}
            <select aria-label="field-execution-environment" value={newEnv} onChange={(e) => setNewEnv(e.target.value)}>
              {CREATE_ENVIRONMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-execution" disabled={busy}>{busy ? t('m34.exec.scheduling') : t('m34.exec.scheduleBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="execution-action-error">{actionError}</p>}
        {notice && <p aria-label="execution-action-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m34.exec.result')}
          <select aria-label="filter-result" value={result} onChange={(e) => setResult(e.target.value)}>
            {RESULTS.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m34.exec.environment')}
          <select aria-label="filter-environment" value={environment} onChange={(e) => setEnvironment(e.target.value)}>
            {ENVIRONMENTS.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m34.common.loading')}</p> : (
        <>
          <p aria-label="execution-count">{t('m34.exec.count', { n: rows.length })}</p>
          <DataTable<ExecutionRow>
            ariaLabel="execution-table"
            rowKey={(r) => r.testExecutionId}
            rows={rows}
            rowDataAttrs={(r) => ({ 'data-result': r.result })}
            columns={[
              { key: 'executionCode', header: t('m34.common.code') },
              { key: 'environment', header: t('m34.exec.environment') },
              { key: 'result', header: t('m34.exec.result') },
              { key: 'status', header: t('m34.common.status') },
              {
                key: 'actions',
                header: t('m34.common.actions'),
                render: (r) => (
                  <>
                    {(r.status === 'NOT_STARTED' || r.status === 'IN_PROGRESS') && (
                      <>
                        <select aria-label={`result-${r.executionCode}`} value={results[r.testExecutionId] ?? 'PASS'} onChange={(e) => setResults((m) => ({ ...m, [r.testExecutionId]: e.target.value }))}>
                          {RECORD_RESULTS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <Button variant="primary" type="button" aria-label={`record-${r.executionCode}`} onClick={() => void act(r, 'record', t('m34.exec.act.recorded'), 'M34_EXECUTION_MANAGE', { result: results[r.testExecutionId] ?? 'PASS' })}>{t('m34.exec.btnRecord')}</Button>
                        <Button variant="danger" type="button" aria-label={`cancel-${r.executionCode}`} onClick={() => void act(r, 'cancel', t('m34.exec.act.cancelled'), 'M34_EXECUTION_MANAGE')}>{t('m34.exec.btnCancel')}</Button>
                      </>
                    )}
                    {r.status === 'COMPLETED' && (
                      <Button variant="secondary" type="button" aria-label={`approve-${r.executionCode}`} onClick={() => void act(r, 'approve', t('m34.exec.act.approved'), 'M34_EXECUTION_APPROVE')}>{t('m34.exec.btnApprove')}</Button>
                    )}
                  </>
                ),
              },
            ]}
          />
        </>
      )}
    </main>
  );
}
